//
// # PDK Devices — the GenAlpha generator <-> OpenAccess cell correspondence
//
// GenAlpha names a device by the GENERATOR that makes it (`nwres_b` in `gf12`);
// OpenAccess names it by a (lib, cell) (`cmos14lpp/nwres_b`). `PDK.devices` is
// the curated statement that the two are the same device, read here in BOTH
// directions from the same table so they cannot drift:
//
//   export: generator ident -> (lib, cell)     `deviceMapFromPdk`
//           (lib, cell)     -> generator entry `generatorForOaCell`
//
// The alternative was a `genalpha.targetIdent` appdef on the OA instance,
// rejected because it puts GenAlpha bookkeeping in the customer's file and
// makes correctness depend on our marker surviving foreign tools (G23).
//
// Lives in `GenAlphaSchema` because both directions need it and neither
// `OaExport` nor `NoaLib` can import the other. The generated-ident codec moved
// here for the same reason — it was in `DepotDirectory`, above both, which
// would have forced a second copy of the format.
//

import { validate as v } from "CoreUtils";
import type { HierPathIdent, Ident, LibCellView, StackId } from "./core";
import type { DeviceMapping, DeviceParamMapping, PDK } from "./pdk";
import type { Content } from "./content";
import { SCHEMA_VERSION } from "./version";

// ---------------------------------------------------------------------------
// Generated idents
// ---------------------------------------------------------------------------

/**
 * Generator config embedded in the ident path.
 */
export interface GeneratorConfig {
  stackId: string;
  params: Record<string, unknown>;
}

/** Validator for GeneratorConfig - used for type-safe decoding */
export const GeneratorConfigValidator = {
  validator: new v.Validator(v.createValidate<GeneratorConfig>()),
};

/** Encode a generator config for an ident path. Params are sorted so the same
 * configuration always produces the same ident. */
export function encodeGeneratorConfig(config: GeneratorConfig): string {
  // Sort params for deterministic encoding
  const sortedParams = Object.keys(config.params)
    .sort()
    .reduce<Record<string, unknown>>((acc, key) => {
      acc[key] = config.params[key];
      return acc;
    }, {});

  const normalized: GeneratorConfig = {
    stackId: config.stackId,
    params: sortedParams,
  };

  return Buffer.from(JSON.stringify(normalized)).toString("base64url");
}

/** Decode a generator config from an ident path, or undefined if it is not one. */
export function decodeGeneratorConfig(
  encoded: string
): GeneratorConfig | undefined {
  try {
    const json = Buffer.from(encoded, "base64url").toString("utf8");
    const parsed: unknown = JSON.parse(json);
    const validated = GeneratorConfigValidator.validator.validate(parsed);
    if (validated.ok) {
      return validated.val;
    }
    return undefined;
  } catch {
    return undefined;
  }
}

// ---------------------------------------------------------------------------
// Generator revision
// ---------------------------------------------------------------------------

/**
 * The revision of generated layouts: the schema version plus a count of
 * changes to what generators emit (1: pins carry their metal as shapes).
 * Stored generated content of any other revision is stale and regenerated,
 * never reused, so bump the count when generated geometry or pins change.
 */
export const GENERATOR_REVISION = `${SCHEMA_VERSION}.1`;

const GENERATOR_REVISION_PROPERTY = "generatorRevision";

/** `content` marked as produced by the current generators. */
export function stampGeneratorRevision(content: Content): Content {
  if (content.contentKind !== "layout") return content;
  return {
    ...content,
    properties: [
      ...(content.properties ?? []).filter(
        (p) => p.name !== GENERATOR_REVISION_PROPERTY
      ),
      { name: GENERATOR_REVISION_PROPERTY, value: GENERATOR_REVISION },
    ],
  };
}

/** Whether stored generated content came from the current generators. */
export function isCurrentGeneration(content: Content): boolean {
  if (content.contentKind !== "layout") return true;
  return (content.properties ?? []).some(
    (p) =>
      p.name === GENERATOR_REVISION_PROPERTY && p.value === GENERATOR_REVISION
  );
}

/**
 * Build a generated ident for a generator output.
 * Format: ["generated", "generators", pdkId, deviceName, configBase64]
 *
 * This is the single source of truth for generated ident construction.
 * Used by both GeneratorRegistry and tools that need to reference generated content.
 */
export function buildGeneratedIdent(
  pdkId: string,
  deviceName: string,
  stackId: string,
  params: Record<string, unknown>
): HierPathIdent {
  const config: GeneratorConfig = { stackId, params };
  const configBase64 = encodeGeneratorConfig(config);
  return {
    kind: "hierpath",
    path: ["generated", "generators", pdkId, deviceName, configBase64],
  };
}

/**
 * The device an external-generator ident names, or `undefined` if it is not one.
 *
 * Format: `["cc_cell_<pdkId>", "<device>_prim[_<stackId>]", "layout"]`, as
 * written by `GeneratorRegistry.buildExternalCellIdent`.
 *
 * `_prim` is required: older `cc_cell_<pdk>/<cell>/layout` fixtures without it
 * are single-instance wrappers, not devices. The device ends at the first
 * `_prim`, since a stack id is itself `_`-delimited.
 */
export function externalCellDevice(
  path: ReadonlyArray<string>
): { pdkId: string; device: string } | undefined {
  if (path.length < 3) return undefined;
  if (!path[0].startsWith("cc_cell_")) return undefined;
  if (path[2] !== "layout") return undefined;
  const cut = path[1].indexOf("_prim");
  if (cut < 0) return undefined;
  // Empty, or the start of a stack id: `foo_primary` is not the device `foo`.
  const tail = path[1].slice(cut + "_prim".length);
  if (tail !== "" && !tail.startsWith("_")) return undefined;
  const pdkId = path[0].slice("cc_cell_".length);
  const device = path[1].slice(0, cut);
  if (pdkId === "" || device === "") return undefined;
  return { pdkId, device };
}

// ---------------------------------------------------------------------------
// External (Custom Compiler) cell idents
// ---------------------------------------------------------------------------

const EXTERNAL_CELL_LIB_PREFIX = "cc_cell_";
const EXTERNAL_CELL_DEVICE_SUFFIX = "_prim";

/**
 * Build the durable ident an external-generator cell's DEFAULT-config layout is
 * saved and looked up under.
 * Format: ["cc_cell_<pdkId>", "<deviceName>_prim_<stackId>", "layout"]
 *
 * Keyed by PDK + device + STACK. It was pdk+device only, which was wrong the
 * moment `generate()` started forcing the design's `stackId` through as Custom
 * Compiler's `stackName` param: the geometry a device generates is
 * stack-dependent, and GF12/GF22 each ship six-plus stacks. Under the old ident
 * a device generated for one stack was handed verbatim to a design on another,
 * with the wrong stack's `stackId` stamped on it.
 *
 * Still not keyed by params: this ident is the device's plain-default config
 * only. `generateAndCache` routes any real caller override to the
 * params-addressed `buildGeneratedIdent` instead.
 */
export function buildExternalCellIdent(
  pdkId: string,
  deviceName: string,
  stackId: StackId
): HierPathIdent {
  return {
    kind: "hierpath",
    path: [
      `${EXTERNAL_CELL_LIB_PREFIX}${pdkId}`,
      `${deviceName}${EXTERNAL_CELL_DEVICE_SUFFIX}_${stackId}`,
      "layout",
    ],
  };
}

// ---------------------------------------------------------------------------
// OA-cell device names
// ---------------------------------------------------------------------------

// Separates the library from the cell in an OA-cell device name. OA names are
// `[A-Za-z0-9_-]`, so a `.` can only be this separator.
const OA_CELL_DEVICE_SEPARATOR = ".";

// An OA library or cell name that can go into an OA-cell device name: not
// empty, and without the separator, which would make the name unparseable.
function isOaNamePart(part: string): boolean {
  return part !== "" && !part.includes(OA_CELL_DEVICE_SEPARATOR);
}

/**
 * The device name Custom Compiler builds an OA schematic's foundry cell under:
 * `<lib>.<cell>`, e.g. `cmos14lpp.nfet_stack`.
 *
 * It names the library as well as the cell because the bare cell name is not
 * enough. Five gf12 cells (`ncap`, `nwres_b`, …) share a name with a native
 * generator, and neither the OA save nor a restarted server could recover the
 * library from a cell name alone. It goes into the existing ident shapes
 * unchanged, as their `<device>` segment.
 *
 * `undefined` when either part is empty or contains the separator.
 */
export function oaCellDeviceName(
  lib: string,
  cell: string
): string | undefined {
  if (!isOaNamePart(lib) || !isOaNamePart(cell)) return undefined;
  return `${lib}${OA_CELL_DEVICE_SEPARATOR}${cell}`;
}

/**
 * The (lib, cell) an OA-cell device name was built from, or `undefined` if
 * `name` is not one — a native or allowlisted generator name has no separator.
 */
export function parseOaCellDeviceName(
  name: string
): { lib: string; cell: string } | undefined {
  const parts = name.split(OA_CELL_DEVICE_SEPARATOR);
  if (parts.length !== 2) return undefined;
  const [lib, cell] = parts;
  if (!isOaNamePart(lib) || !isOaNamePart(cell)) return undefined;
  return { lib, cell };
}

/**
 * The foundry cell a reference to an OA-cell device names, or `undefined`.
 *
 * Its `<lib>.<cell>` device name already says which OA cell it is, so no
 * device table is consulted. Recognizes the device in each form a reference
 * takes (matches the Rust `oa_cell_target`):
 *   - generator `["generators", <pdkId>, <device>]`                    -> symbol
 *   - generated `["generated","generators",<pdkId>,<device>,<config>]` -> layout
 *   - external  `["cc_cell_<pdkId>","<device>_prim_<stackId>","layout"]` -> layout
 */
export function oaCellTarget(ident: Ident): LibCellView | undefined {
  if (ident.kind !== "hierpath") return undefined;
  const p = ident.path;
  let device: string | undefined;
  let view: string;
  if (p[0] === "generators" && p.length === 3) {
    device = p[2];
    view = "symbol";
  } else if (p[0] === "generated" && p[1] === "generators" && p.length >= 4) {
    device = p[3];
    view = "layout";
  } else {
    device = externalCellDevice(p)?.device;
    view = "layout";
  }
  const oaCell =
    device === undefined ? undefined : parseOaCellDeviceName(device);
  return oaCell && { lib: oaCell.lib, cell: oaCell.cell, view };
}

/**
 * The generator path an instance `target` was produced from, or `undefined`
 * when no generator made it.
 *
 * Inverse of every ident `generateAndCache` can return, in one place because
 * two callers previously carried their own copies of it: `Design.getInstance`
 * (which decides whether the Properties Panel gets a param spec at all) and
 * `LayoutInstanceBuilder.update` (which decides whether a param edit
 * regenerates geometry). Both knew the two `generators` shapes and neither
 * learned about the external-cell shape, so a default-placed Custom Compiler
 * device showed no editable params and, if params reached it anyway, kept a
 * stale target while accepting the new values.
 *
 * The bare `<deviceName>_prim` form is accepted alongside `_prim_<stackId>`
 * so instances saved before the ident gained its stack id stay editable.
 */
export function generatorPathForTarget(target: Ident): string[] | undefined {
  if (target.kind !== "hierpath") return undefined;
  const path = target.path;

  // Direct generator: ["generators", pdkId, deviceName]
  if (path[0] === "generators" && path.length === 3) {
    return [...path];
  }

  // Params-addressed output: ["generated", "generators", pdkId, deviceName, config]
  if (path[0] === "generated" && path[1] === "generators" && path.length >= 4) {
    return ["generators", path[2], path[3]];
  }

  // Default-config external cell:
  // ["cc_cell_<pdkId>", "<deviceName>_prim_<stackId>", "layout"]
  if (path.length === 3 && path[0].startsWith(EXTERNAL_CELL_LIB_PREFIX)) {
    const pdkId = path[0].slice(EXTERNAL_CELL_LIB_PREFIX.length);
    // First occurrence of the suffix ends the device name: stack ids are
    // themselves `_`-delimited, so splitting on the last `_` would keep only a
    // fragment. The tail must then be empty (the pre-stackId form) or start the
    // stack id, so a device actually named e.g. `foo_primary` is not mistaken
    // for `foo`.
    const cut = path[1].indexOf(EXTERNAL_CELL_DEVICE_SUFFIX);
    const tail = path[1].slice(cut + EXTERNAL_CELL_DEVICE_SUFFIX.length);
    if (pdkId && cut > 0 && (tail === "" || tail.startsWith("_"))) {
      return ["generators", pdkId, path[1].slice(0, cut)];
    }
  }

  return undefined;
}

// ---------------------------------------------------------------------------
// The device table, read both ways
// ---------------------------------------------------------------------------

/** A resolver: GenAlpha device ident -> real OA cell, or `undefined` when this
 * PDK's table does not name the device. */
export type DeviceToOa = (ident: Ident) => LibCellView | undefined;

/**
 * What an OA (lib, cell) resolves back to: the generator that makes it, the
 * GenAlpha parameter values that CHOOSING this cell is evidence for, and the
 * parameter correspondence for the rest.
 */
export interface OaDeviceMatch {
  /** GenAlpha generator name within the PDK, e.g. "nmos". */
  generator: string;
  /** GA parameters implied by the cell itself, e.g. `{vt: "lvt"}`. */
  when: Record<string, string | number | boolean>;
  /** Per-parameter name/type correspondence; empty for a fixed-geometry cell. */
  params: Array<DeviceParamMapping>;
}

/** A resolver: an OA (lib, cell) -> the GenAlpha device it implements, or
 * `undefined` when the table does not claim one. */
export type OaToGenerator = (
  lib: string,
  cell: string
) => OaDeviceMatch | undefined;

// ---------------------------------------------------------------------------
// Parameter values, in a form this package can name
// ---------------------------------------------------------------------------
//
// `noa.Param` lives in `NoaSchema`, which this package must not depend on — it
// sits below everything so both legs can reach it. So the translation deals in
// a neutral value and `NoaLib` converts at its own boundary.

/** An OpenAccess parameter value, in the four variants a device uses. */
export type OaParamValue =
  | { kind: "string"; value: string }
  | { kind: "int"; value: number }
  | { kind: "double"; value: number }
  | { kind: "bool"; value: boolean };

/** One OpenAccess instance parameter. */
export interface OaParam {
  name: string;
  value: OaParamValue;
}

// Read the table once, keeping only entries that identify exactly one side. An
// entry naming both a generator and a builtin, or neither, is skipped rather
// than guessed at — either reading could put an instance on the wrong cell.
function usableEntries(pdk: PDK): Array<DeviceMapping> {
  const out: Array<DeviceMapping> = [];
  for (const d of pdk.devices ?? []) {
    const hasGen = d.generator !== undefined && d.generator !== "";
    const hasBuiltin = d.builtin !== undefined && d.builtin !== "";
    if (hasGen === hasBuiltin) continue;
    out.push(d);
  }
  return out;
}

// Does every value this entry requires appear, with that value, in the
// reference's parameters? No `when` means unconditional.
//
// Strict `===`, no coercion: this picks a CELL, so letting `"1"` satisfy `1`
// would choose a different device rather than report a mismatch.
function whenMatches(
  entry: DeviceMapping,
  gaParams: Record<string, unknown>
): boolean {
  if (entry.when === undefined) return true;
  for (const [name, want] of Object.entries(entry.when)) {
    if (gaParams[name] !== want) return false;
  }
  return true;
}

// The entries for one generator, most specific first, so a `when`-guarded cell
// is preferred over the unconditional fallback beside it.
function byGeneratorName(pdk: PDK): Map<string, Array<DeviceMapping>> {
  const out = new Map<string, Array<DeviceMapping>>();
  for (const d of usableEntries(pdk)) {
    if (d.generator === undefined || d.generator === "") continue;
    const list = out.get(d.generator);
    if (list) list.push(d);
    else out.set(d.generator, [d]);
  }
  for (const list of out.values()) {
    list.sort(
      (a, b) =>
        Object.keys(b.when ?? {}).length - Object.keys(a.when ?? {}).length
    );
  }
  return out;
}

/**
 * Build the FORWARD resolver: a GenAlpha device reference -> its OA cell.
 *
 * Recognizes the three forms a device is referenced by:
 *   - builtin   `{kind:"builtin", id}`                    -> the symbol view
 *   - generator `["generators", <pdkId>, <device>]`        -> the symbol view
 *   - generated `["generated","generators",<pdkId>,<device>,<config>]` -> layout
 *
 * The generated form carries its parameters in the trailing config blob, so a
 * generator with several cells (one per threshold flavour, say) is resolved by
 * decoding it and matching each entry's `when`. The bare generator form carries
 * no parameters at all, so a multi-cell generator CANNOT be resolved from it —
 * that reference is refused rather than resolved to an arbitrary flavour, and
 * falls back to the provisional by-name mapping, which is what it did before
 * any of these entries existed. Resolving it properly means reading a schematic
 * instance's own params, which the ident does not carry.
 *
 * Returns `undefined` when the PDK carries no device table at all, so a caller
 * can fall back to its own naming.
 */
export function deviceMapFromPdk(pdk: PDK): DeviceToOa | undefined {
  const entries = usableEntries(pdk);
  if (entries.length === 0) return undefined;

  const byGenerator = byGeneratorName(pdk);
  const byBuiltin = new Map<string, { lib: string; cell: string }>();
  for (const d of entries) {
    if (d.builtin !== undefined && d.builtin !== "") {
      byBuiltin.set(d.builtin, { lib: d.lib, cell: d.cell });
    }
  }

  // Resolved against whatever parameters the reference carries. `undefined` if
  // no `when` is satisfied, or if several cells exist and nothing chooses.
  const pick = (
    generator: string,
    gaParams: Record<string, unknown>,
    view: string
  ): LibCellView | undefined => {
    const list = byGenerator.get(generator);
    if (list === undefined || list.length === 0) return undefined;
    if (list.length === 1 && list[0].when === undefined) {
      return { lib: list[0].lib, cell: list[0].cell, view };
    }
    const hit = list.find((d) => whenMatches(d, gaParams));
    return hit ? { lib: hit.lib, cell: hit.cell, view } : undefined;
  };

  return (ident: Ident): LibCellView | undefined => {
    // Custom Compiler built it from the OA cell its name carries.
    const oaCell = oaCellTarget(ident);
    if (oaCell !== undefined) return oaCell;
    if (ident.kind === "builtin") {
      const t = byBuiltin.get(ident.id);
      return t ? { ...t, view: "symbol" } : undefined;
    }
    if (ident.kind !== "hierpath") return undefined;
    const p = ident.path;
    // Schematic form: ["generators", <pdkId>, <device>] -> the symbol view. No
    // parameters travel in this form, so only an unconditional entry can match.
    if (p[0] === "generators" && p.length >= 3) {
      return pick(p[2], {}, "symbol");
    }
    // Layout form: ["generated","generators",<pdkId>,<device>,<config>] -> the
    // layout view of the same cell, chosen by the config's own parameters.
    if (p[0] === "generated" && p[1] === "generators" && p.length >= 4) {
      const config = p.length >= 5 ? decodeGeneratorConfig(p[4]) : undefined;
      return pick(p[3], config?.params ?? {}, "layout");
    }
    // External-generator form: ["cc_cell_<pdkId>","<device>_prim","layout"] ->
    // the layout view of the cell Custom Compiler drew it from.
    //
    // No parameters: the ident carries none, so OA builds the instance at its
    // CDF defaults, which may differ from the depot geometry (the exporter
    // reports this in `unsupported`).
    const external = externalCellDevice(p);
    if (external !== undefined) {
      return pick(external.device, {}, "layout");
    }
    return undefined;
  };
}

// The map key for an OA (lib, cell). One function, not two template literals:
// they are only correct if they agree, and while writing this they did not (NUL
// on one side, a space on the other) — invisible in a diff, and every lookup
// missed. `\u0000` cannot occur in an OA name.
const cellKey = (lib: string, cell: string): string => `${lib}\u0000${cell}`;

/**
 * Build the INVERSE resolver: an OA (lib, cell) -> the GenAlpha device.
 *
 * An OA cell claimed by more than one generator is REFUSED rather than resolved
 * by picking the first: nothing in the schema enforces that `(lib, cell)` is
 * unique across entries, and choosing one would silently place a device on the
 * wrong generator. A refusal leaves the instance as a plain `libcellview`, which
 * is what it already was. Two entries naming the SAME generator with different
 * `when` values are refused for the same reason — the cell would no longer be
 * evidence for one set of parameters, which is the whole point of the
 * discriminator.
 *
 * Builtin entries are deliberately not inverted. A builtin is a GenAlpha
 * primitive with no geometry of its own, so it cannot stand in for a placed OA
 * layout instance; only generators can.
 */
export function generatorForOaCell(pdk: PDK): OaToGenerator {
  const byCell = new Map<string, OaDeviceMatch | null>(); // null marks ambiguity
  for (const d of usableEntries(pdk)) {
    if (d.generator === undefined || d.generator === "") continue;
    const key = cellKey(d.lib, d.cell);
    if (byCell.has(key)) {
      byCell.set(key, null);
      continue;
    }
    byCell.set(key, {
      generator: d.generator,
      when: d.when ?? {},
      params: d.params ?? [],
    });
  }
  return (lib: string, cell: string): OaDeviceMatch | undefined => {
    // `null` marks an ambiguous key, and it deserves the same answer as a
    // miss: leave the instance as the OA cell reference it already is.
    return byCell.get(cellKey(lib, cell)) ?? undefined;
  };
}

// ---------------------------------------------------------------------------
// Parameters, translated through the table's rows
// ---------------------------------------------------------------------------

// A GenAlpha value rendered into the variant the PCell declares. `undefined`
// when it cannot be — a data error in the mapping, not something to coerce past:
// OA refuses a mismatched datatype and fails the whole write.
function encodeOne(
  row: DeviceParamMapping,
  value: unknown
): OaParamValue | undefined {
  switch (row.oaType) {
    case "bool":
      return typeof value === "boolean" ? { kind: "bool", value } : undefined;
    case "int":
      return typeof value === "number" && Number.isInteger(value)
        ? { kind: "int", value }
        : undefined;
    case "double":
      return typeof value === "number" ? { kind: "double", value } : undefined;
    case "string":
      if (typeof value === "boolean") {
        // A GA boolean carried as one of two agreed spellings. Both must be
        // present, or the encoding cannot be inverted.
        if (row.trueText === undefined || row.falseText === undefined) {
          return undefined;
        }
        return { kind: "string", value: value ? row.trueText : row.falseText };
      }
      if (typeof value === "number") {
        return { kind: "string", value: String(value) };
      }
      return typeof value === "string" ? { kind: "string", value } : undefined;
  }
  // Every variant `oaType` declares returns above, and the absence of a
  // `default` is what makes that enforced: `switch-exhaustiveness-check` fails
  // the build when a variant is added and not handled here. Reaching this line
  // means the PDK carried an `oaType` outside the union, which only unvalidated
  // data can do — treated as unmappable, the same as a value that will not
  // encode, rather than thrown on.
  return undefined;
}

/** The outcome of translating a device's GenAlpha parameters for OpenAccess. */
export interface EncodedDeviceParams {
  /** Parameters to put on the OA instance. */
  params: Array<OaParam>;
  /**
   * GenAlpha parameter names that did not travel — no row names them, or the
   * value could not be expressed in the declared variant. The device reaches
   * OpenAccess at the foundry's default for each, which is a real divergence
   * and is why this is returned rather than dropped.
   */
  unmapped: Array<string>;
}

/**
 * Translate a device's GenAlpha parameters into OpenAccess instance parameters.
 *
 * Parameters named by the entry's `when` are deliberately NOT written: they are
 * carried by the choice of cell, and writing them as well would either be
 * rejected (the foundry has no such parameter) or state the same thing twice.
 */
export function encodeDeviceParams(
  match: OaDeviceMatch,
  gaParams: Record<string, unknown>
): EncodedDeviceParams {
  const params: Array<OaParam> = [];
  const named = new Set<string>(Object.keys(match.when));
  // A set: one GA parameter can have several rows (`ndum` writes both sides of
  // the device), and a failure would otherwise be reported once per row.
  const unmapped = new Set<string>();

  for (const row of match.params) {
    named.add(row.ga);
    const value = gaParams[row.ga];
    if (value === undefined) continue; // nothing to say about it
    const encoded = encodeOne(row, value);
    if (encoded === undefined) {
      unmapped.add(row.ga);
      continue;
    }
    params.push({ name: row.oa, value: encoded });
  }

  for (const name of Object.keys(gaParams)) {
    if (!named.has(name)) unmapped.add(name);
  }
  return { params, unmapped: [...unmapped] };
}
